import { useState, useMemo } from 'react';
import { FiMail } from 'react-icons/fi';
import SubscriptionDetailsModal from './SubscriptionDetailsModal';
import { filterArray } from 'utilities/FilterArray';

interface SubscribersTableProps {
  filters: {
    status: string;
    customer: string;
    subscriptionType: string;
    billingPeriod: string;
    paymentStatus: string;
    startDate: Date | null;
    endDate: Date | null;
    search: string;
  };
}

const subscribersData = [
  {
    customerName: 'John Doe',
    company: 'ABC',
    subscription: 'Plan 1',
    status: 'Cancelled',
    startDate: '12/03/2025',
    endDate: '12/03/2025',
    invoiceDate: '12/03/2025',
    dueDate: '12/06/2024',
    paymentStatus: 'Failed',
    paymentMethod: 'Credit card'
  },
  {
    customerName: 'Donna May',
    company: 'INTER',
    subscription: 'Plan 2 - Trial',
    status: 'Trial',
    startDate: '12/03/2025',
    endDate: '12/03/2025',
    invoiceDate: 'NA',
    dueDate: 'NA',
    paymentStatus: 'Pending',
    paymentMethod: 'NA'
  },
  {
    customerName: 'John Doe',
    company: 'ABC',
    subscription: 'Plan 1',
    status: 'Cancelled',
    startDate: '12/03/2025',
    endDate: '12/03/2025',
    invoiceDate: 'NA',
    dueDate: 'NA',
    paymentStatus: 'Paid',
    paymentMethod: 'Credit card'
  },    
  {
    customerName: 'Rajesh Kumar',
    company: 'INTER',
    subscription: 'Plan 2 - Trial',
    status: 'Trial',
    startDate: '12/03/2025',
    endDate: '12/03/2025',
    invoiceDate: 'NA',
    dueDate: 'NA',
    paymentStatus: 'Paid',
    paymentMethod: 'Credit card'
  },
  {
    customerName: 'Amit Kumar',
    company: 'INTER',
    subscription: 'Plan 2 - Trial',
    status: 'Trial',
    startDate: '12/03/2025',
    endDate: '12/03/2025',  
    invoiceDate: 'NA',  
    dueDate: 'NA',
    paymentStatus: 'Paid',
    paymentMethod: 'Credit card'
  },
  // Add more subscribers...
];

export default function SubscribersTable({ filters }: SubscribersTableProps) {
  const [selectedSubscriber, setSelectedSubscriber] = useState<any>(null);

  // Filter subscribers based on all filters
  const filteredSubscribers = useMemo(() => {
    let filtered = [...subscribersData];

    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(subscriber => 
        subscriber.customerName.toLowerCase().includes(searchTerm) ||
        subscriber.company.toLowerCase().includes(searchTerm) ||
        subscriber.subscription.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(subscriber => 
        subscriber.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    // Filter by subscription type
    if (filters.subscriptionType) {
      filtered = filtered.filter(subscriber => 
        subscriber.subscription.toLowerCase() === filters.subscriptionType.toLowerCase()
      );
    }

    // Filter by billing period
    if (filters.billingPeriod) {
      // filtered = filtered.filter(subscriber => 
      //   subscriber.billingPeriod.toLowerCase() === filters.billingPeriod.toLowerCase()
      // );
    }

    // Filter by payment status
    if (filters.paymentStatus) {
      filtered = filtered.filter(subscriber => 
        subscriber.paymentStatus.toLowerCase() === filters.paymentStatus.toLowerCase()
      );
    }

    // Filter by date range
    if (filters.startDate && filters.endDate) {
      filtered = filtered.filter(subscriber => {
        const subscriberDate = new Date(subscriber.startDate);
        return subscriberDate >= filters.startDate! && 
               subscriberDate <= filters.endDate!;
      });
    }

    return filtered;
  }, [filters]);

  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'failed': return 'bg-[#FF0000]/40 text-[#fff]';
      case 'pending': return 'bg-[#FFA500]/40 text-[#fff]';
      case 'paid': return 'bg-[#008000]/40 text-[#fff]';
      case 'cancelled': return 'bg-[#FF0000]/40 text-[#fff]';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <>
      {/* Sticky Header */}
      <div className="sticky top-[100px] bg-black  pt-4">
        <div className="bg-[#2A2A2A] rounded-t-lg overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                <th className="text-left p-4">Customer name</th>
                <th className="text-left p-4">Company</th>
                <th className="text-left p-4">Subscription</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Start Date</th>
                <th className="text-left p-4">End Date</th>
                <th className="text-left p-4">Invoice Date</th>
                <th className="text-left p-4">Due Date</th>
                <th className="text-left p-4">Payment Status</th>
                <th className="text-left p-4">Payment method</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      {/* Table Body */}
      <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
        <table className="w-full table-fixed">
          <tbody>
            {filteredSubscribers.map((subscriber, index) => (
              <tr key={index} className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]">
                <td className="p-4 text-white cursor-pointer" onClick={() => setSelectedSubscriber(subscriber)}>{subscriber.customerName}</td>
                <td className="p-4 text-gray-400 cursor-pointer " onClick={() => setSelectedSubscriber(subscriber)}>{subscriber.company}</td>
                <td 
                  className="p-4 text-gray-400 cursor-pointer hover:text-white"
                  onClick={() => setSelectedSubscriber(subscriber)}
                >
                  {subscriber.subscription}
                </td>
                <td className="p-4 text-gray-400 cursor-pointer" onClick={() => setSelectedSubscriber(subscriber)}>{subscriber.status}</td>
                <td className="p-4 text-gray-400">{subscriber.startDate}</td>
                <td className="p-4 text-gray-400">{subscriber.endDate}</td>
                <td className="p-4 text-gray-400">{subscriber.invoiceDate}</td>
                <td className="p-4 text-red-400">{subscriber.dueDate}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusStyle(subscriber.paymentStatus)}`}>
                    {subscriber.paymentStatus}
                  </span>
                </td>
                <td className="p-4 text-gray-400">{subscriber.paymentMethod}</td>
                <td className="p-4 text-right">
                  <button className="text-gray-400 hover:text-white">
                    <FiMail size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedSubscriber && (
        <SubscriptionDetailsModal
          isOpen={!!selectedSubscriber}
          onClose={() => setSelectedSubscriber(null)}
          subscriber={selectedSubscriber}
        />
      )}
    </>
  );
} 